// @funkit/connect-core
//
// Headless, platform-agnostic business logic shared by @funkit/connect (web)
// and @funkit/connect-rn (React Native): domains, utils, hooks, Statsig
// wrappers, theme factories, and the FunLogger class.
//
// INVARIANT: no DOM, vanilla-extract, or wagmi imports anywhere under src/**.
// See ../connect-rn/README.md for the core/web/RN architecture.
//
// PUBLIC API: this is an explicit, curated export list (not `export *`) — the
// package's consumers are its sibling packages @funkit/connect and
// @funkit/connect-rn, and this surface is exactly what they import. Adding a
// symbol here is a deliberate, reviewable widening of the public API; a new
// `export` inside a module no longer leaks out automatically.

export { default as defaultDynamicConfigs } from './src/__generated__/default_configs'
export { default as defaultFeatureGates } from './src/__generated__/default_feature_gates'
export {
  DEFAULT_NOTIF_CUTOFF,
  getUpdatedCheckouts,
  getUpdatedDirectExecutions,
  isBitcoinLightningDirectExecution,
  isTokenTransferDirectExecution,
  isWithdrawalDirectExecution,
  mergeMultiStepDirectExecutions,
  processRecentDepositsForNotifications,
} from './src/domains/checkoutNotifications'
export type {
  MergedMultiStepDirectExecution,
  NotificationVariant,
} from './src/domains/checkoutNotifications'
export {
  MAX_BTC_LIGHTNING_AMOUNT_USD,
  MELD_MIN_LIMIT_BUFFER,
  convertUsdAmount,
  getCheckoutAmountLimitsUsd,
  getConfiguredMinDepositUsd,
  getMeldMinDepositInCurrency,
  getMinTransferValueEur,
  getMinTransferValueUsd,
  validateWithdrawalAmount,
} from './src/domains/amountLimits'
export type {
  CheckoutAmountLimitsUsd,
  MinDepositTarget,
  WithdrawalAmountValidation,
} from './src/domains/amountLimits'
export {
  effectiveDepositTarget,
  getCheckoutTokenTransferParams,
  getMinTransferValueForChain,
} from './src/domains/checkoutTransfer'
export type {
  CheckoutTokenTransferConfig,
  DepositTarget,
  MinTransferConfig,
  UDAConfig,
} from './src/domains/checkoutTransfer'
export type {
  AssetClass,
  DynamicTargetAssetCandidate,
  TokenInfo,
} from './src/domains/dynamicTargetAsset'
export {
  CryptoWithdrawalError,
  createWithdrawalSubmissionId,
  processCryptoWithdrawalOrder,
} from './src/domains/cryptoWithdrawal'
export type {
  CryptoWithdrawalErrorCode,
  CryptoWithdrawalOrder,
  CryptoWithdrawalToken,
  ProcessCryptoWithdrawalOrderParams,
} from './src/domains/cryptoWithdrawal'
export { PaymentMethod } from './src/domains/paymentMethods'
export {
  extractRelayFeeInfo,
  extractRelayReceiveAmount,
  fetchExactInQuote,
} from './src/domains/quote'
export type { ExactInQuoteParams } from './src/domains/quote'
export type {
  FailureReason,
  FiatProvider,
  FiatStepResponse,
  FlowState,
  FormDescriptor,
  FormField,
  JsonValue,
  KYCProvider,
  KycFieldId,
  OrderStatus,
  Quote,
  SelectOption,
  Surface,
  Transition,
} from '@fun-xyz/fiat-contract/types'
export { createIdentityAssertionProvider } from './src/domains/fiat/identityAssertion'
export type {
  GetIdentityAssertion,
  IdentityAssertionProvider,
} from './src/domains/fiat/identityAssertion'
export {
  enteredStep,
  isEntryOwnedState,
  rendersOwnControl,
  selectEntryCta,
  shouldRenderCta,
  submittableTransition,
  surfaceControl,
} from './src/domains/fiat/entryCta'
export type {
  EnterControl,
  EntryControl,
  EntryCta,
  SubmitControl,
  SurfaceControl,
} from './src/domains/fiat/entryCta'
export { selectKycOutcome } from './src/domains/fiat/kycOutcome'
export type { KycOutcome, KycResolution } from './src/domains/fiat/kycOutcome'
export {
  KYC_SDK_TOKEN_ENDPOINT,
  renewCaptureSurface,
} from './src/domains/fiat/kycSdkToken'
export type { RenewCaptureSurfaceParams } from './src/domains/fiat/kycSdkToken'
export { requestCheckoutClientSecret } from './src/domains/fiat/stripeCheckout'
export type { CheckoutClientSecretParams } from './src/domains/fiat/stripeCheckout'
export {
  canSubmitForm,
  formValuesInput,
  isValueField,
  requiresUnanswerableField,
  validateForm,
} from './src/domains/fiat/formValues'
export type { FormFieldIssue, FormValues } from './src/domains/fiat/formValues'
export { resolveFiatRegionSupport } from './src/domains/fiat/regionPolicy'
export type {
  FiatRegionGeo,
  FiatRegionPolicy,
  FiatRegionVerdict,
} from './src/domains/fiat/regionPolicy'
export { isDirectFiatDelivery } from './src/domains/fiat/directDelivery'
export type { FiatAssetRef } from './src/domains/fiat/directDelivery'
export {
  isQuotableAmount,
  isStaleQuoteFailure,
  PAYMENT_SESSION_ENDPOINT,
  QUOTE_ENDPOINT,
  sameAmount,
} from './src/domains/fiat/quoteRequest'
export type {
  FiatQuoteMaximum,
  FiatQuoteMinimum,
  FiatRoutingOverrides,
  QuoteGeo,
  QuoteRequest,
} from './src/domains/fiat/quoteRequest'
export {
  forgetRouteLimits,
  ROUTE_LIMITS_SCOPE,
} from './src/domains/fiat/routeLimits'
export type { RouteLimits } from './src/domains/fiat/routeLimits'
export {
  acceptedTerms,
  termsToAccept,
} from './src/domains/fiat/termsAcceptance'
export type { TermsToAccept } from './src/domains/fiat/termsAcceptance'
export type {
  AwaitTransition,
  SubmitTransition,
  SurfaceTransition,
} from './src/domains/fiat/selectTransitions'
export type {
  CollectedInputs,
  SurfaceResult,
} from './src/domains/fiat/submitBody'
export { useFiatQuote } from './src/hooks/fiat/useFiatQuote'
export type {
  UseFiatQuoteParams,
  UseFiatQuoteResult,
} from './src/hooks/fiat/useFiatQuote'
export { useFiatTransitions } from './src/hooks/fiat/useFiatTransitions'
export type {
  FiatFlow,
  FiatIdentityAssertion,
  UseFiatTransitionsParams,
} from './src/hooks/fiat/useFiatTransitions'
// Named in this package's own signatures (the quote a withdrawal executes), so
// consumers can hold one without depending on @funkit/utils directly.
export type { CheckoutQuoteResponse } from '@funkit/utils'
export {
  FOPS_STALE_TIME,
  buildSwappedThirdPartyPaymentConfigs,
  getFopsLimit,
  getSwappedDefaultInputAmount,
  getWithdrawalFopsDisclaimerLimit,
  roundFopsLimit,
  sortFops,
} from './src/domains/swapped'
export type {
  SwappedDefaultInputAmountConfig,
  SwappedThirdPartyPaymentConfigsParams,
} from './src/domains/swapped'
export {
  FiatWithdrawalError,
  SWAPPED_MIN_SELL_EUR,
  buildSwappedSellLimits,
  findSwappedSourceOverride,
  getSwappedCashMinUsd,
  getSwappedSellCapEur,
  isValidOrderData,
  processFiatWithdrawalOrder,
  resolveSwappedWithdrawalLegs,
  toFiatWithdrawalOrder,
  toWithdrawalAmountBaseUnit,
} from './src/domains/swappedWithdrawal'
export type {
  FiatWithdrawalErrorCode,
  FiatWithdrawalOrder,
  ProcessFiatWithdrawalOrderParams,
  SwappedSellLimits,
  SwappedSourceRouteOverride,
  SwappedWithdrawalLegs,
  SwappedWithdrawalSourceToken,
  SwappedWithdrawalTargetToken,
} from './src/domains/swappedWithdrawal'
export { BLOCKED_RISK_LEVELS, screenAddressesForRisk } from './src/domains/risk'
export type { AddressRiskScreening } from './src/domains/risk'
export {
  isBlacklistedWithdrawalAddress,
  isWithdrawalRisky,
} from './src/domains/withdrawalRisk'
export {
  createSyntheticFop,
  getFopKey,
  isSwappedFop,
  isSyntheticFop,
} from './src/domains/syntheticFop'
export { ensureRelayClientInitialized } from './src/domains/withdrawal'
export {
  type WalletExecutionErrorCode,
  toWalletExecutionErrorCode,
} from './src/domains/walletError'
export type {
  ListFormOfPayment,
  SyntheticFormOfPayment,
} from './src/domains/syntheticFop'
export {
  isAssetDisabled,
  isChainDisabled,
  resolveTokenTransferConfig,
} from './src/domains/tokenTransferConfig'
export type {
  DisabledChainsAndAssets,
  ResolvedAssetInfo,
  TokenTransferAssetConfig,
  TokenTransferChainConfig,
  TokenTransferConfigResult,
} from './src/domains/tokenTransferConfig'
export {
  getLighterAccount,
  useInvalidateLighterAccount,
  useLighterAccountQuery,
} from './src/hooks/queries/useLighterAccountQuery'
export type {
  LighterAccount,
  LighterAccountLookup,
  LighterAccountResponse,
  LighterAsset,
  LighterPoolInfo,
  LighterShare,
} from './src/hooks/queries/useLighterAccountQuery'
export {
  LIGHTER_TRADING_MODE,
  getLighterAccountsByL1Address,
  useLighterAccountsQuery,
} from './src/hooks/queries/useLighterAccountsQuery'
export type {
  LighterAccountsByL1AddressResponse,
  LighterSubAccount,
} from './src/hooks/queries/useLighterAccountsQuery'
export { useDirectExecutionForDepositAddress } from './src/hooks/queries/useDirectExecutionForDepositAddress'
export type {
  DepositAddressArrival,
  DirectExecutionForDepositAddressParams,
} from './src/hooks/queries/useDirectExecutionForDepositAddress'
export { useRecentCheckoutsQuery } from './src/hooks/queries/useRecentCheckoutsQuery'
export { useRecentDepositsApiQuery } from './src/hooks/queries/useRecentDepositsApiQuery'
export type { RecentDepositsApiQueryParams } from './src/hooks/queries/useRecentDepositsApiQuery'
export {
  DEFAULT_FIAT_CURRENCY,
  useSwappedDefaultCurrencyQuery,
} from './src/hooks/queries/useSwappedDefaultCurrencyQuery'
export {
  useDynamicConfig,
  useExperiment,
  useFeatureGate,
} from './src/hooks/statsig'
export type { DynamicConfigName, DynamicConfigValue } from './src/hooks/statsig'
export { useDebouncedValue } from './src/hooks/useDebouncedValue'
export { useEffectEvent } from './src/hooks/useEffectEvent'
export { useLogOnce } from './src/hooks/useLogOnce'
export { useSwappedSession } from './src/hooks/useSwappedSession'
export { useUserIpInfo } from './src/hooks/useUserIpInfo'
export type {
  FunkitUserIpInfo,
  UseUserIpInfoParams,
  UseUserIpInfoResult,
} from './src/hooks/useUserIpInfo'
export type {
  LogContext,
  LogRecord,
  LogTransport,
  TrackingConsent,
} from './src/interfaces/logTransport'
export type {
  SwappedInboundMessage,
  SwappedOutboundMessage,
  SwappedSellOrderData,
  SwappedTransport,
} from './src/interfaces/swappedTransport'
export type {
  WithdrawalClient,
  WithdrawalTransaction,
  WithdrawalTypedData,
} from './src/interfaces/withdrawalClient'
export {
  getWalletLabel,
  isAddressCompatibleWithChain,
  isEvmChainId,
  isWithdrawalAddressSupported,
  normalizeRecipientAddress,
  toValidatedSolanaAddress,
} from './src/utils/address'
export { getAssetPriceInfoWithFallback } from './src/utils/assetPrice'
export {
  SDK_CDN_IMAGES_URL,
  getChainIconUrl,
  toChainCdnKey,
} from './src/utils/chainIconUrl'
export {
  isCheckoutHistoryDirectExecution,
  isCheckoutValid,
  toMergedCheckoutHistoryItem,
} from './src/utils/checkout'
export type { MergedCheckoutHistoryItem } from './src/utils/checkout'
export {
  disableAtomicBatchIfSupported,
  findDirectExecutionForDepositAddress,
  getCompletionTxInfo,
  resolveOnChainCompletionTxHash,
  getDirectExecutionCreatedTimeMs,
  getDirectExecutionNotificationKey,
  getDirectExecutionRunTimeSeconds,
  getDirectExecutionUpdatedTimeMs,
  isDirectExecutionNotFound,
} from './src/utils/directExecution'
export { FunLogger } from './src/utils/funLogger'
export { getExchangeRate } from './src/utils/getExchangeRate'
export { getPublicClientFor } from './src/utils/publicClient'
export { purifyCheckoutHistoryItem } from './src/utils/purifyCheckoutHistoryItem'
export { randomUuid } from './src/utils/randomUuid'
export type { PurifiedCheckoutHistoryItem } from './src/utils/purifyCheckoutHistoryItem'
export {
  isTerminalRelayStatus,
  mapRelayExecutionStatusToCheckoutState,
} from './src/utils/relayStatus'
export { safeBigInt } from './src/utils/safeBigInt'
export { isSwitchChainError, isUserRejectedError } from './src/utils/signature'
// Re-exported so connect-rn need not depend on @funkit/utils directly.
export { isAddressEquivalent, isTokenEquivalent } from '@funkit/utils'
export {
  SWAPPED_CARD_BRANDS,
  getFormOfPaymentDisplayName,
  getSavedFormOfPaymentDisplayName,
  getSavedFormOfPaymentIcon,
  getSwappedIcon,
  getSwappedOrigin,
  isSavedFormOfPaymentType,
  isSwappedOrigin,
} from './src/utils/swapped'
export type { SwappedPillAmounts, SwappedTheme } from './src/utils/swapped'
export {
  getDepositAddressForChain,
  getTransferTokenQrCodeUri,
  isTransferChainEnabled,
} from './src/utils/transfer'
export type { QRCodeUri, TransferTokenQrCodeType } from './src/utils/transfer'
export {
  USD_DECIMALS,
  deductFlatFee,
  percentOfAmount,
  resolveWithdrawalSourceTokenBalance,
  tokenAmountToUsd,
  usdToTokenAmount,
} from './src/utils/withdrawal'
